// #807 Fase B (R21) · el gate de confirmación del mapeo inferido, por firma.
//
// ⚠️ F3 (revisor externo, 23-sep-2026): la primera versión guardaba las
// firmas confirmadas bajo UNA clave fija de `localStorage`, sin dueño y sin
// purga al cerrar sesión. El comentario decía "la primera vez que ESTE
// MÉDICO ve una firma inferida"; lo implementado era "este NAVEGADOR". En un
// computador compartido de hospital, el médico B heredaba las confirmaciones
// del médico A y nunca veía el único control humano que existe sobre un
// mapeo automático — el mismo patrón (clave sin dueño que sobrevive al
// logout) que ya causó INC-2026-32 en `turno-draft-buffer.js` (memoria
// `turno_draft_buffer_cruce_phi`), acá sobre otra superficie.
//
// A diferencia de ese buffer, ACÁ no hay PHI (el valor es una firma
// estructural del formato: delimitador + cantidad de columnas, nunca texto
// clínico), así que no hace falta `rekeyDrafts`: perder la confirmación al
// rotar el token solo cuesta un click de más, nunca un dato clínico.
const PREFIX = "censo_firmas_confirmadas.v1:";

const storage = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    // Safari en modo privado puede tirar al tocar localStorage.
    return null;
  }
};

// Dueño de la clave: el `authToken` crudo de sesión, igual que
// `turno-draft-buffer.js` — inmediato desde el primer click, sin esperar a
// que `currentUser` resuelva su GET async. Sin sesión (no debería pasar en
// /turno, detrás de `RequireAuth`) cae a un valor fijo que ningún token real
// pisa por accidente.
const ownerToken = () => {
  try {
    return localStorage.getItem("authToken") || "sin-sesion";
  } catch {
    return "sin-sesion";
  }
};

const keyDeOwner = (owner) => `${PREFIX}${encodeURIComponent(owner)}`;

const leerFirmas = () => {
  const ls = storage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(keyDeOwner(ownerToken()));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

export const estaFirmaConfirmada = (firma) => {
  if (!firma) return false;
  return leerFirmas().includes(firma);
};

export const confirmarFirma = (firma) => {
  if (!firma) return;
  const ls = storage();
  if (!ls) return;
  try {
    const actuales = leerFirmas();
    if (!actuales.includes(firma)) {
      actuales.push(firma);
      ls.setItem(keyDeOwner(ownerToken()), JSON.stringify(actuales));
    }
  } catch {
    /* degradado: sin storage, se vuelve a pedir el click la próxima vez */
  }
};

/**
 * Llamado desde `logout()` (`authentication-helpers.js`), al lado de
 * `purgarDictado()`/`purgarDrafts()`. Barre TODAS las claves con el prefijo
 * — no solo la del owner actual — como defensa en profundidad si el token se
 * reutiliza o si algo lee `localStorage` entre sesiones.
 */
export const purgarFirmasConfirmadas = () => {
  const ls = storage();
  if (!ls) return;
  const keys = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (key && key.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach((key) => {
    try {
      ls.removeItem(key);
    } catch {
      /* nada que hacer */
    }
  });
};

export const __testing = { PREFIX, ownerToken, keyDeOwner };
